"use client";

import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Load } from "@/components/load";
import PageError from "@/components/page-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HistoryTimeline } from "@/pages/financial/term-validation/view/components/history-timeline";
import { format } from "date-fns";
import {
  ChevronLeft,
  CheckCircle,
  XCircle,
  Loader2,
  KeyRound,
  Download,
  Upload,
} from "lucide-react";
import {
  useGetTermByIdQuery,
  useGetTermPdfQuery,
  useGetTermHistoryQuery,
  useUpdateTermStatusMutation,
  useGetTermDocumentsQuery,
  useReplaceSignedTermMutation,
} from "@/services/generate-term/generate-term";
import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { TermRequest } from "@/pages/financial/term-validation/data/types";
import FileViewer from "@/pages/financial/term-validation/view/components/file-viewer";
import { useSignedUrlMutation } from "@/services/file";
import {
  UserAccessConfigCard,
  UserAccessConfigCardRef,
} from "@/pages/tax/access-request/edit/components/config";
import { UserResponsibleTypeEnum } from "@/services/dtos/enums/accessAutomation.enum";
import { CreateAccessRequest } from "@/services/dtos/accessAutomation.dto";
import { formatNumericStringToCPF } from "@/utils/format-cps";
import { useCreateAccessMutation } from "@/services/access-automation";
import { StatusEnumAccessRequest } from "@/enum/access_request";
import {
  useCreateRequestMutation,
  useUpdatedRequestMutation,
} from "@/services/request-access";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import AccessCreationErrorModal from "./components/access-creation-error-modal";
import CancelTermModal from "./components/cancel-term-modal";
import ReplaceSignedTermModal, {
  ReplaceSignedTermSubmission,
} from "./components/replace-signed-term-modal";
import { useCanReplaceSignedTerm } from "./hooks/use-can-replace-signed-term";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { accessSchema } from "@/services/access-automation/schemas";
import { ZodIssue } from "zod";
import { TERM_REJECT_TOOLTIPS } from "@/pages/financial/term-validation/utils/constants";
import { NON_CANCELABLE_TERM_STATUSES, TERM_STATUS } from "../utils/constants";
import { translateReason, translateStatus } from "../utils/translations";
import {
  MESSAGES,
  REPLACE_SIGNED_TERM_LABELS,
  TERM_HISTORY_COMMENTS,
  buildApprovalHistoryComment,
} from "../utils/messages";

export function ViewTermValidation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { handleModal } = useDialogModal();

  const goBack = () => {
    if (location.key === "default") {
      navigate("/financeiro/validacao-de-termos");
    } else {
      navigate(-1);
    }
  };

  // Access Creation
  const configRef = useRef<UserAccessConfigCardRef>(null);
  const [createAccess] = useCreateAccessMutation();
  const [createRequest] = useCreateRequestMutation();
  const [updatedRequest] = useUpdatedRequestMutation();

  const [updateTermStatus] = useUpdateTermStatusMutation();
  const [replaceSignedTerm] = useReplaceSignedTermMutation();
  const canReplaceSignedTerm: boolean = useCanReplaceSignedTerm();
  const [comment, setComment] = useState("");
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [signedUrl] = useSignedUrlMutation();
  const [isApprovingTerm, setIsApprovingTerm] = useState(false);
  const [isRejectingTerm, setIsRejectingTerm] = useState(false);
  const [isApprovingSignature, setIsApprovingSignature] = useState(false);
  const [isRejectingSignature, setIsRejectingSignature] = useState(false);
  const [isApprovingWithoutUser, setIsApprovingWithoutUser] = useState(false);

  const { data: documents } = useGetTermDocumentsQuery(id || "", { skip: !id });
  const signedTermFileId: string | undefined = documents?.signedTerm?.id;

  const {
    data: term,
    isLoading: isLoadingTerm,
    error: termError,
  } = useGetTermByIdQuery(id || "", {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: history,
    isLoading: isLoadingHistory,
    error: historyError,
  } = useGetTermHistoryQuery(id || "", {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const getFileUrl = useCallback(async () => {
    if (term?.status === TERM_STATUS.PENDING) {
      setFileUrl(null);
      return;
    }

    if (signedTermFileId) {
      try {
        const response = await signedUrl({
          id: signedTermFileId,
          ResponseContentDisposition: "inline",
        });

        if (response?.data?.url) {
          setFileUrl(response.data.url);
        }
      } catch (error) {
        console.error("Erro ao obter a URL do arquivo", error);
      }
    }
  }, [signedUrl, signedTermFileId, term?.status]);

  useEffect(() => {
    getFileUrl();
  }, [getFileUrl]);

  const {
    data: pdfContent,
    isLoading: isLoadingPdf,
    error: pdfError,
  } = useGetTermPdfQuery(id || "", { skip: !id });

  const isLoading = isLoadingTerm || isLoadingPdf || isLoadingHistory;
  const hasError = termError || pdfError || historyError;

  const handleCreateUserAccess = useCallback(async (): Promise<
    string | undefined
  > => {
    try {
      // 1. Dados brutos
      const rawTerm = {
        user_name: term?.user_name,
        cpf: term?.cpf,
        store_code: term?.store_code,
      };

      // 2. Dados de configuração
      const configData = configRef.current?.getConfigData();
      if (!configData)
        throw new Error(
          "Dados de configuração de acesso ausentes ou inválidos",
        );

      // 3. Validação consolidada
      const parseResult = accessSchema.safeParse({
        term: rawTerm,
        config: configData,
      });
      if (!parseResult.success) {
        parseResult.error.issues.forEach((issue: ZodIssue) => {
          const [section, field] = issue.path;
          if (section === "config") {
            field === "userPassword"
              ? configData.setUserPasswordError([issue.message])
              : field === "operatorPassword" &&
                configData.setOperatorPasswordError([issue.message]);
          }
        });
        throw new Error("Erros de validação. Veja os campos destacados.");
      }

      // 4. Dados tipados
      const {
        term: { user_name, cpf, store_code },
        config: { userClassValue, userPassword, operatorPassword },
      } = parseResult.data;

      // 5. Formatação + payload
      const formattedCpf = formatNumericStringToCPF(cpf);
      const createAccessRequestBody: CreateAccessRequest = {
        body: {
          create: {
            cpf: formattedCpf,
            storeCode: Number(store_code),
            userResponsibleType: userClassValue,
            userPassword: userPassword.value,
            operatorPassword: operatorPassword.value,
            isManager: true, // Flag para ignorar bloqueio de GERENTE (vem do termo)
          },
          // Não passa update pois o access_request é criado depois (linhas abaixo)
        },
      };

      // 6. Mutations
      const accessResult = await createAccess(createAccessRequestBody).unwrap();
      if (!accessResult)
        throw new Error("Houve um erro ao criar o acesso para usuário.");

      const createdId = await createRequest({
        name: user_name,
        cpf: formattedCpf,
        store: String(store_code),
      }).unwrap();
      if (!createdId)
        throw new Error("Houve um erro ao criar o acesso para usuário.");

      await updatedRequest({
        comment: "Requisição de acesso finalizada.",
        id: createdId,
        status: StatusEnumAccessRequest.COMPLETED as never,
        access_details: accessResult,
      }).unwrap();

      toast(MESSAGES.USER_ACCESS_CREATED);
      return createdId;
    } catch (error: any) {
      console.error("Error creating user access:", error);
      const messages = error?.data?.messages;
      const apiMessage = Array.isArray(messages)
        ? messages.join(". ")
        : error?.data?.message || error?.message || "Erro desconhecido.";
      throw new Error(apiMessage);
    }
  }, [term, id, createAccess, createRequest, updatedRequest, toast]);

  if (isLoading) {
    return <Load />;
  }

  if (hasError || !term) {
    return <PageError status={500} message={"ERRO INTERNO NO SERVIDOR"} />;
  }

  const handleApproveTerm = async () => {
    try {
      setIsApprovingTerm(true);
      await updateTermStatus({
        termId: id!,
        status: TERM_STATUS.TERM_PENDING_SIGNATURE,
        comment,
      }).unwrap();
      goBack();
      toast(MESSAGES.APPROVE_TERM.SUCCESS);
    } catch (error) {
      console.error("Erro ao aprovar termo", error);
      toast(MESSAGES.APPROVE_TERM.ERROR);
    } finally {
      setIsApprovingTerm(false);
    }
  };

  const handleRejectTerm = async () => {
    if (!comment) {
      toast(MESSAGES.COMMENT_REQUIRED);
      return;
    }
    try {
      setIsRejectingTerm(true);
      await updateTermStatus({
        termId: id!,
        status: TERM_STATUS.REJECTED,
        comment,
      }).unwrap();
      goBack();
      toast(MESSAGES.REJECT_TERM.SUCCESS);
    } catch (error) {
      console.error("Erro ao rejeitar termo", error);
      toast(MESSAGES.REJECT_TERM.ERROR);
    } finally {
      setIsRejectingTerm(false);
    }
  };

  const handleFinalApproval = async () => {
    setIsApprovingSignature(true);
    try {
      const access_request_id = await handleCreateUserAccess();
      if (!access_request_id) {
        throw new Error("Erro ao criar acesso para usuário.");
      }

      await updateTermStatus({
        termId: id!,
        status: TERM_STATUS.APPROVED,
        comment: buildApprovalHistoryComment(
          TERM_HISTORY_COMMENTS.APPROVED_WITH_USER,
          comment,
        ),
        access_request_id,
      }).unwrap();

      goBack();
      toast(MESSAGES.APPROVE_TERM.SUCCESS);
    } catch (error: any) {
      console.error("Erro ao aprovar termo", error);
      const apiMessage = error?.data?.message || error?.message;
      await handleModal({
        isOpen: true,
        element: (
          <AccessCreationErrorModal
            handleModal={handleModal}
            errorMessage={apiMessage}
          />
        ),
        title: "Não foi possível aprovar o termo.",
      });
    } finally {
      setIsApprovingSignature(false);
    }
  };

  const handleApproveWithoutUserCreation = async () => {
    setIsApprovingWithoutUser(true);
    try {
      await updateTermStatus({
        termId: id!,
        status: TERM_STATUS.APPROVED,
        comment: buildApprovalHistoryComment(
          TERM_HISTORY_COMMENTS.APPROVED_WITHOUT_USER,
          comment,
        ),
      }).unwrap();

      goBack();
      toast(MESSAGES.APPROVE_TERM.SUCCESS);
    } catch (error) {
      console.error("Erro ao aprovar termo", error);
      toast(MESSAGES.APPROVE_TERM.ERROR);
    } finally {
      setIsApprovingWithoutUser(false);
    }
  };

  const handleReplaceSignedTerm = async ({
    file,
    comment: reason,
    createUser,
  }: ReplaceSignedTermSubmission): Promise<void> => {
    try {
      const access_request_id: string | undefined = createUser
        ? await handleCreateUserAccess()
        : undefined;
      if (createUser && !access_request_id) {
        throw new Error("Erro ao criar acesso para usuário.");
      }
      await replaceSignedTerm({
        termId: id!,
        file,
        comment: reason,
        access_request_id,
      }).unwrap();
      handleModal({ isOpen: false });
      goBack();
      toast(MESSAGES.REPLACE_SIGNED_TERM.SUCCESS);
    } catch (error) {
      toast({
        variant: "destructive",
        description: extractRtkErrorMessage(
          error,
          MESSAGES.REPLACE_SIGNED_TERM.ERROR,
        ),
      });
    }
  };

  const handleReplaceSignedTermModal = (): void => {
    handleModal({
      isOpen: true,
      element: <ReplaceSignedTermModal onConfirm={handleReplaceSignedTerm} />,
      title: REPLACE_SIGNED_TERM_LABELS.TITLE,
    });
  };

  const handleRejectSigned = async () => {
    if (!comment) {
      toast(MESSAGES.COMMENT_REQUIRED);
      return;
    }
    try {
      setIsRejectingSignature(true);
      await updateTermStatus({
        termId: id!,
        status: TERM_STATUS.TERM_PENDING_SIGNATURE,
        comment,
      }).unwrap();
      goBack();
      toast(MESSAGES.REJECT_SIGNATURE.SUCCESS);
    } catch (error) {
      console.error("Erro ao rejeitar assinatura", error);
      toast(MESSAGES.REJECT_SIGNATURE.ERROR);
    } finally {
      setIsRejectingSignature(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return "R$ 0,00";
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "-";
    try {
      return format(new Date(date), "dd/MM/yyyy");
    } catch {
      return "-";
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      APPROVED: "bg-green-500/90 text-white",
      REJECTED: "bg-red-500/90 text-white",
      PENDING: "bg-amber-500/90 text-white",
      TERM_SIGNED: "bg-blue-500/90 text-white",
      TERM_PENDING_SIGNATURE: "bg-orange-500/90 text-white",
      CANCELED: "bg-gray-500/90 text-white",
    };

    return (
      <span
        className={`${
          variants[status as keyof typeof variants] ||
          "bg-gray-500/90 text-white"
        } px-3 py-1 rounded-full text-sm font-medium`}
        style={{ pointerEvents: "none" }}
      >
        {translateStatus(status)}
      </span>
    );
  };

  const handleDownload = async () => {
    try {
      if (signedTermFileId) {
        const response = await signedUrl({ id: signedTermFileId }).unwrap();
        if (response?.url) {
          window.open(response.url, "_blank");
        }
        toast(MESSAGES.FILE_DOWNLOAD.SUCCESS);
      }
    } catch (error) {
      console.error("Erro ao tentar obter a URL para download:", error);
      toast(MESSAGES.FILE_DOWNLOAD.ERROR);
    }
  };

  const handleCancelModal = () => {
    handleModal({
      isOpen: true,
      element: <CancelTermModal id={term.id} />,
      title: "Cancelar Termo",
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => goBack()}
              className="rounded-full h-10 w-10 p-0 shadow-sm hover:shadow transition-all"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-800">
              Detalhes do Termo
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {!NON_CANCELABLE_TERM_STATUSES.includes(term.status) && (
              <Button
                variant="destructive"
                size="sm"
                className="h-8 flex items-center gap-1.5 shadow-sm hover:shadow transition-all"
                onClick={handleCancelModal}
              >
                <XCircle className="w-4 h-4" />
                Cancelar Termo
              </Button>
            )}
            {term.status && getStatusBadge(term.status)}
          </div>
        </div>

        <div className="grid gap-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="bg-gray-50 border-b pb-3">
                <CardTitle className="text-xl text-gray-800">
                  Informações do Termo
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="store"
                    className="text-sm font-medium text-gray-600"
                  >
                    Loja
                  </Label>
                  <Input
                    id="store"
                    value={term?.store_code || "-"}
                    readOnly
                    className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="user_name"
                    className="text-sm font-medium text-gray-600"
                  >
                    Nome
                  </Label>
                  <Input
                    id="user_name"
                    value={term?.user_name || "-"}
                    readOnly
                    className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="reason"
                    className="text-sm font-medium text-gray-600"
                  >
                    Motivo
                  </Label>
                  <Input
                    id="reason"
                    value={translateReason(term?.reason || "-")}
                    readOnly
                    className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="cash_balance"
                    className="text-sm font-medium text-gray-600"
                  >
                    Saldo em Caixa
                  </Label>
                  <Input
                    id="cash_balance"
                    value={formatCurrency(term?.cash_balance)}
                    readOnly
                    className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="treasury_audit_date"
                    className="text-sm font-medium text-gray-600"
                  >
                    Data da Auditoria
                  </Label>
                  <Input
                    id="treasury_audit_date"
                    value={formatDate(term?.treasury_audit_date)}
                    readOnly
                    className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="justification"
                    className="text-sm font-medium text-gray-600"
                  >
                    Justificativa
                  </Label>
                  <Textarea
                    id="justification"
                    value={term?.justification || "-"}
                    readOnly
                    className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20 min-h-[100px] resize-none"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="bg-gray-50 border-b pb-3">
                <CardTitle className="text-xl text-gray-800">
                  Histórico do Termo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <HistoryTimeline history={history || []} />
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card className="overflow-hidden border-none shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="flex flex-row items-center justify-between bg-gray-50 border-b pb-3">
                <CardTitle className="text-xl text-gray-800">
                  Visualização do Termo
                </CardTitle>
                {pdfContent && (
                  <Button
                    onClick={() => {
                      if (
                        (term.status as TermRequest["status"]) ===
                          TERM_STATUS.PENDING ||
                        (term.status as TermRequest["status"]) ===
                          TERM_STATUS.REJECTED ||
                        (term.status as TermRequest["status"]) ===
                          TERM_STATUS.TERM_PENDING_SIGNATURE
                      ) {
                        const link = document.createElement("a");
                        link.href = `data:application/pdf;base64,${pdfContent}`;
                        link.download = `termo-${term.user_name}.pdf`;
                        link.click();
                      } else {
                        handleDownload();
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="h-8 flex items-center gap-1.5 shadow-sm hover:shadow transition-all"
                  >
                    <Download className="h-4 w-4" />
                    <span>Baixar Termo</span>
                  </Button>
                )}
              </CardHeader>

              {(term.status as TermRequest["status"]) === TERM_STATUS.PENDING ||
              (term.status as TermRequest["status"]) === TERM_STATUS.REJECTED ||
              (term.status as TermRequest["status"]) ===
                TERM_STATUS.TERM_PENDING_SIGNATURE ? (
                <div className="p-4 bg-gray-100 rounded-b-lg">
                  <iframe
                    src={`data:application/pdf;base64,${pdfContent}#toolbar=0&view=FitH`}
                    className="w-full h-[600px] border rounded-md shadow-sm bg-white"
                  />
                </div>
              ) : term.status === TERM_STATUS.APPROVED ||
                (term.status as TermRequest["status"]) ===
                  TERM_STATUS.TERM_SIGNED ? (
                <FileViewer fileUrl={fileUrl!} />
              ) : null}
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="bg-gray-50 border-b pb-3">
                <CardTitle className="text-xl text-gray-800">Ações</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {term.status === TERM_STATUS.PENDING ? (
                  <>
                    <div className="space-y-2">
                      <Label
                        htmlFor="comment"
                        className="text-sm font-medium text-gray-600"
                      >
                        Anotações
                      </Label>
                      <Input
                        id="comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Adicione um comentário sobre sua decisão"
                        className="border-gray-200 focus-visible:ring-primary/20"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4 items-center justify-center">
                      <Button
                        className="font-medium bg-white hover:bg-red-50 text-red-600 border border-red-200 hover:border-red-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        variant="outline"
                        onClick={handleRejectTerm}
                        disabled={
                          isRejectingTerm || isApprovingTerm || !comment.trim()
                        }
                        title={
                          !comment.trim()
                            ? TERM_REJECT_TOOLTIPS.TERM
                            : undefined
                        }
                      >
                        {isRejectingTerm ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Rejeitando...
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Rejeitar
                          </>
                        )}
                      </Button>
                      <Button
                        className="font-medium bg-green-500 hover:bg-green-600 text-white shadow-sm"
                        onClick={handleApproveTerm}
                        disabled={isRejectingTerm || isApprovingTerm}
                      >
                        {isApprovingTerm ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Aprovando...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aprovar
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (term.status as TermRequest["status"]) ===
                  TERM_STATUS.TERM_SIGNED ? (
                  <>
                    <div className="space-y-2">
                      <UserAccessConfigCard
                        isSection={true}
                        defaultClass={UserResponsibleTypeEnum.MANAGER}
                        allowedClasses={[UserResponsibleTypeEnum.MANAGER]}
                        ref={configRef}
                      />
                      <Label
                        htmlFor="comment"
                        className="text-sm font-medium text-gray-600"
                      >
                        Anotações
                      </Label>
                      <Input
                        id="comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Adicione um comentário sobre sua decisão"
                        className="border-gray-200 focus-visible:ring-primary/20"
                      />
                    </div>
                    <div className="flex flex-col gap-3 pt-4">
                      <Button
                        className="font-medium bg-white hover:bg-red-50 text-red-600 border border-red-200 hover:border-red-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        variant="outline"
                        onClick={handleRejectSigned}
                        disabled={
                          isRejectingSignature ||
                          isApprovingSignature ||
                          !comment.trim()
                        }
                        title={
                          !comment.trim()
                            ? TERM_REJECT_TOOLTIPS.SIGNATURE
                            : undefined
                        }
                      >
                        {isRejectingSignature ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Recusando...
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Recusar Assinatura
                          </>
                        )}
                      </Button>
                      <Button
                        className="w-full font-medium bg-white hover:bg-green-50 text-green-700 border border-green-200 hover:border-green-300 shadow-sm"
                        variant="outline"
                        onClick={handleApproveWithoutUserCreation}
                        disabled={
                          isRejectingSignature ||
                          isApprovingSignature ||
                          isApprovingWithoutUser
                        }
                      >
                        {isApprovingWithoutUser ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Aprovando...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aprovar Termo sem Criar Usuário
                          </>
                        )}
                      </Button>
                      <Button
                        className="w-full font-medium bg-green-500 hover:bg-green-600 text-white shadow-sm"
                        onClick={handleFinalApproval}
                        disabled={
                          isRejectingSignature ||
                          isApprovingSignature ||
                          isApprovingWithoutUser
                        }
                      >
                        {isApprovingSignature ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Aprovando...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aprovar Termo e Criar Usuário
                          </>
                        )}
                      </Button>
                      {canReplaceSignedTerm && (
                        <Button
                          className="w-full font-medium bg-white hover:bg-amber-50 text-amber-700 border border-amber-200 hover:border-amber-300 shadow-sm"
                          variant="outline"
                          onClick={handleReplaceSignedTermModal}
                          disabled={
                            isRejectingSignature ||
                            isApprovingSignature ||
                            isApprovingWithoutUser
                          }
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {REPLACE_SIGNED_TERM_LABELS.TITLE}
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="items-center justify-center">
                    <div className="text-center">
                      <p className="text-xl text-gray-500 bg-gray-50/80 px-6 py-4 rounded-lg border border-gray-100 shadow-sm">
                        Este termo já foi{" "}
                        <span className="font-semibold">
                          {(term.status as TermRequest["status"]) ===
                          TERM_STATUS.TERM_PENDING_SIGNATURE
                            ? "aprovado e aguarda assinatura"
                            : term.status === TERM_STATUS.REJECTED
                              ? "rejeitado"
                              : term.status === TERM_STATUS.APPROVED
                                ? "aprovado"
                                : "pendente"}
                        </span>
                        {!isLoadingTerm && term?.access_request_id && (
                          <div className="mt-4 text-base space-y-2">
                            <h2>Confira as informações de acesso:</h2>
                            <Button
                              onClick={() =>
                                navigate(
                                  "/financeiro/requisicao-de-acesso/" +
                                    term.access_request_id +
                                    "/editar",
                                )
                              }
                            >
                              <span>Configurações de acesso</span>
                              <KeyRound size={20} className="ml-2" />
                            </Button>
                          </div>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
